import { useEffect, useState } from "react";
import { Button } from "@optimizely/axiom";
import { AccessManagementUserFormAccessHeader } from "../AccessManagementUserForm/AccessManagementUserFormAccessHeader";
import { AddProductAccessForm, AddProductAccessPermission } from "../AddProductAccess/AddProductAccessForm";
import { AddGroupAccessForm } from "../AddGroupAccess/AddGroupAccessForm";
import { CustomGroupAccessConfirmationModal } from "../CustomGroupAccessConfirmation/CustomGroupAccessConfirmationModal";
import { AccessDetailsTable } from "../AccessDetailsTable/AccessDetailsTable";
import { useAccessFlowUserFormContext } from "../AccessManagementUserForm/AccessManagementUserFormProvider";
import LimitByRole from "../../LimitByRole/LimitByRole";
import { AccessDetailsByProductId, AccessDetailsRow } from "../../../../domain/AccessDetails";
import {
    addGroupWithoutProductAccess,
    grantsNoProductAccess,
    UserGroupWithProjectDetails
} from "../../../../lib/access-helpers";
import { GroupsWithoutProductAccessList } from "../GroupsWithoutProductAccess/GroupsWithoutProductAccessList";
import {
    ADMINCENTER_CUSTOM_GROUP_ACCESS_REVIEW_EVENT_NAME,
    ALL_PROJECTS_ID,
    ALL_PROJECTS_NAME,
    ATTRIBUTE_ROLES,
    GROUP_TYPES
} from "../../../../constants/constants";

/**
 * Product/group access selector for the bulk user upload flow.
 *
 * Uses the same single-select product access form as the single-invite flow (AddProductAccessForm:
 * one product -> one instance -> one role), plus custom-group access. There is no single user (the
 * chosen access is applied to every user in the uploaded CSV), so it works without an email. The
 * committed selections are emitted upward as AccessDetailsByProductId.
 */
export const BulkProductAccessForm = ({
    organizationId,
    onAccessDetailsChange,
    onGroupsWithoutProductAccessChange,
    className
}: {
    organizationId?: string;
    onAccessDetailsChange?: (access: AccessDetailsByProductId) => void;
    onGroupsWithoutProductAccessChange?: (groups: UserGroupWithProjectDetails[]) => void;
    className?: string;
}) => {
    const [accessFormType, setAccessFormType] = useState<"Product" | "Custom Group" | undefined>(undefined);
    const [accessDetailsByProductId, setAccessDetailsByProductId] = useState<AccessDetailsByProductId>({});
    // Groups that grant no product access have no product key to live under (EPTS-6429).
    const [groupsWithoutProductAccess, setGroupsWithoutProductAccess] = useState<UserGroupWithProjectDetails[]>([]);
    const { updateCustomGroupAccess, setUpdateCustomGroupAccess } = useAccessFlowUserFormContext();

    useEffect(() => {
        onAccessDetailsChange?.(accessDetailsByProductId);
    }, [accessDetailsByProductId, onAccessDetailsChange]);

    useEffect(() => {
        onGroupsWithoutProductAccessChange?.(groupsWithoutProductAccess);
    }, [groupsWithoutProductAccess, onGroupsWithoutProductAccessChange]);

    // Custom-group review handshake (mirrors InvitationAccessForm).
    useEffect(() => {
        const handleEvent = (e: CustomEvent) => {
            const { action, userGroup } = e.detail;
            setUpdateCustomGroupAccess({ action, id: userGroup.id, name: userGroup.name, userGroup });
        };
        window.addEventListener(
            ADMINCENTER_CUSTOM_GROUP_ACCESS_REVIEW_EVENT_NAME,
            handleEvent as EventListenerOrEventListenerObject
        );
        return () => {
            window.removeEventListener(
                ADMINCENTER_CUSTOM_GROUP_ACCESS_REVIEW_EVENT_NAME,
                handleEvent as EventListenerOrEventListenerObject
            );
        };
    }, [setUpdateCustomGroupAccess]);

    const closeAccessForm = () => setAccessFormType(undefined);

    const handleSave = ({ permissions }: { permissions: AddProductAccessPermission[] }) => {
        if (!permissions || permissions.length === 0) {
            return;
        }

        setAccessDetailsByProductId((prev) => {
            const updated = { ...prev };

            permissions.forEach((permission) => {
                if (!permission || !permission.product || !permission.instance) {
                    return;
                }

                const { product, instance, group, role, project } = permission;
                const existing = updated[product.id];

                const newRow: AccessDetailsRow = {
                    instanceName: instance.name,
                    roles: [role],
                    nickname: instance.nickname,
                    projectId: role.isInstanceRole ? ALL_PROJECTS_ID : project?.id,
                    projectName: role.isInstanceRole ? ALL_PROJECTS_NAME : project?.name,
                    groupId: group.id,
                    groupName: group.name,
                    isCustomGroup: group.groupType === GROUP_TYPES.CUSTOM
                };

                // Build fresh objects/arrays at each level so we never mutate React state.
                const instanceAccess = { ...(existing?.instanceAccess || {}) };
                instanceAccess[instance.id] = [...(instanceAccess[instance.id] || []), newRow];
                updated[product.id] = { productName: existing?.productName ?? product.name, instanceAccess };
            });

            return updated;
        });

        closeAccessForm();
    };

    const handleRemove = ({ accessDetailsRow }: { accessDetailsRow: AccessDetailsRow }) => {
        setAccessDetailsByProductId((prevDetails) => {
            // Rebuild the structure immutably, dropping every row for the removed group.
            // A custom group can span multiple instances (one row each) under a single groupId,
            // so we remove all matching rows — not just the first — otherwise the table would show
            // lingering access for a group the user thought they removed. Mirrors InvitationAccessForm.
            const updated: AccessDetailsByProductId = {};

            Object.keys(prevDetails).forEach((productId) => {
                const productAccess = prevDetails[productId];
                const newInstanceAccess: AccessDetailsByProductId[string]["instanceAccess"] = {};

                Object.keys(productAccess.instanceAccess).forEach((instanceId) => {
                    const rows = productAccess.instanceAccess[instanceId].filter(
                        (row) => row.groupId !== accessDetailsRow.groupId
                    );
                    if (rows.length > 0) {
                        newInstanceAccess[instanceId] = rows;
                    }
                });

                if (Object.keys(newInstanceAccess).length > 0) {
                    updated[productId] = { ...productAccess, instanceAccess: newInstanceAccess };
                }
            });

            return updated;
        });
    };

    // Kept async to satisfy CustomGroupAccessConfirmationModal's `onSubmit: () => Promise<void>` prop type.
    const submitAddCustomGroupAccess = async () => {
        const customGroupAccessDetails = updateCustomGroupAccess?.accessDetails;

        // Mirrors InvitationAccessForm: an empty accessDetails map is also what the confirmation
        // modal publishes mid-fetch and after a failed fetch, so only classify once the lookup has
        // succeeded. The modal's CTA is disabled until then; this is the backstop (EPTS-6429).
        if (updateCustomGroupAccess?.accessDetailsStatus !== "loaded") {
            return;
        }

        // No product instances means the merge below would be a no-op and the selection would be
        // silently lost, so keep the group in its own slice instead (EPTS-6429).
        if (grantsNoProductAccess(customGroupAccessDetails)) {
            setGroupsWithoutProductAccess((prev) =>
                addGroupWithoutProductAccess(prev, updateCustomGroupAccess?.userGroup)
            );
            setUpdateCustomGroupAccess(undefined);
            setAccessFormType(undefined);
            return;
        }

        if (customGroupAccessDetails) {
            setAccessDetailsByProductId((tableAccess) => {
                const merged = { ...tableAccess };
                Object.keys(customGroupAccessDetails).forEach((productId) => {
                    const incoming = customGroupAccessDetails[productId];
                    const existing = merged[productId];
                    if (!existing) {
                        merged[productId] = incoming;
                        return;
                    }
                    // Clone instanceAccess before writing so we don't mutate React state.
                    const instanceAccess = { ...existing.instanceAccess };
                    Object.keys(incoming.instanceAccess).forEach((instanceId) => {
                        instanceAccess[instanceId] = [
                            ...(instanceAccess[instanceId] || []),
                            ...incoming.instanceAccess[instanceId]
                        ];
                    });
                    merged[productId] = { ...existing, instanceAccess };
                });
                return merged;
            });
        }
        setUpdateCustomGroupAccess(undefined);
        setAccessFormType(undefined);
    };

    const cancelAddCustomGroupAccess = () => setUpdateCustomGroupAccess(undefined);

    return (
        <div {...(className && { className })} data-cy="bulk-product-access-form">
            <AccessManagementUserFormAccessHeader>
                <LimitByRole action={[ATTRIBUTE_ROLES.GROUPS.UPDATE, ATTRIBUTE_ROLES.GROUPS.MANAGE]} mode="hide">
                    <div className="flex justify-content-between align-items-center">
                        <Button
                            className="push--right"
                            // eslint-disable-next-line react/style-prop-object
                            style="outline"
                            isDisabled={!!accessFormType}
                            whiteBackground
                            onClick={() => setAccessFormType("Product")}
                            data-testid="bulk-add-product-access"
                            data-cy="bulk-add-product-access"
                        >
                            Add Product Access
                        </Button>
                        <LimitByRole action={[ATTRIBUTE_ROLES.GROUPS.UPDATE]} mode="hide">
                            <Button
                                className="push--right"
                                // eslint-disable-next-line react/style-prop-object
                                style="outline"
                                isDisabled={!!accessFormType}
                                whiteBackground
                                onClick={() => setAccessFormType("Custom Group")}
                                data-testid="bulk-add-group-access"
                                data-cy="bulk-add-group-access"
                            >
                                Add Group Access
                            </Button>
                        </LimitByRole>
                    </div>
                </LimitByRole>
            </AccessManagementUserFormAccessHeader>

            {accessFormType === "Product" && (
                <AddProductAccessForm
                    existingAccess={accessDetailsByProductId}
                    className="push-double--top push-double--bottom"
                    onSubmit={handleSave}
                    onClose={closeAccessForm}
                    isSaving={false}
                />
            )}

            {accessFormType === "Custom Group" && (
                <AddGroupAccessForm onClose={closeAccessForm} organizationId={organizationId} email="" customGroups />
            )}

            {!!updateCustomGroupAccess && (
                <CustomGroupAccessConfirmationModal
                    action={updateCustomGroupAccess.action}
                    errorMessage=""
                    // submitAddCustomGroupAccess classifies on accessDetails, so confirming before
                    // the lookup resolves would record the wrong thing (EPTS-6429).
                    isConfirmDisabled={updateCustomGroupAccess.accessDetailsStatus !== "loaded"}
                    isSaving={false}
                    userGroup={updateCustomGroupAccess.userGroup!}
                    onClose={cancelAddCustomGroupAccess}
                    onSubmit={submitAddCustomGroupAccess}
                />
            )}

            <AccessDetailsTable accessDetails={accessDetailsByProductId} onRemove={handleRemove} />
            <GroupsWithoutProductAccessList
                className="push--top"
                groups={groupsWithoutProductAccess}
                onRemove={(group) => setGroupsWithoutProductAccess((prev) => prev.filter((g) => g.id !== group.id))}
            />
        </div>
    );
};

BulkProductAccessForm.displayName = "BulkProductAccessForm";
